import React from 'react';
import classnames from 'classnames';
import { FormikProps, withFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { Dropdown, Form, Header } from 'semantic-ui-react';
import styled from 'styled-components';

import { AdminRole, adminRoles } from '@/types/affiliation';
import { emailSchema } from '@/services/validators/email';
import { Label } from '@/components/shared/Label';

type OuterProps = {
  isLoading: boolean;
  inviteAffiliation: (email: string, role: AdminRole) => void;
};

type Values = {
  email: string;
  role: AdminRole;
};

export const InviteForm = withFormik<OuterProps, Values>({
  mapPropsToValues: () => ({
    email: '',
    role: 'member',
  }),
  validationSchema: emailSchema,
  handleSubmit: (values, { props }) => {
    props.inviteAffiliation(values.email, values.role);
  },
  enableReinitialize: true,
})(
  // enhanced form
  ({
    errors,
    handleSubmit,
    handleChange,
    setFieldValue,
    isLoading = false,
    values,
  }: OuterProps & FormikProps<Values>) => {
    const { t } = useTranslation();
    const handleChangeRole = (selectedRole: AdminRole) =>
      setFieldValue('role', selectedRole);

    return (
      <Styled>
        <Form onSubmit={handleSubmit} className="invite-form">
          <Form.Field>
            <Form.Input
              data-test="email"
              className={classnames('field', {
                error: errors.email,
              })}
              fluid={true}
              label={t('models.contact.email')}
              name="email"
              onChange={handleChange}
              value={values.email}
            />
            {errors.email && <Label>{errors.email}</Label>}
          </Form.Field>
          <Form.Field>
            <div className="field">
              <label>{t('models.affiliation.role')}</label>
              <Dropdown
                data-test="roles"
                className="select-role"
                options={adminRoles.map((roleItem, index) => ({
                  'data-test': `${roleItem}-role`,
                  content: (
                    <Header
                      className="select-role-header"
                      content={t(
                        `models.affiliation.roleType.${roleItem}.name`,
                      )}
                      subheader={t(
                        `models.affiliation.roleType.${roleItem}.description`,
                      )}
                    />
                  ),
                  key: index,
                  onClick: () => handleChangeRole(roleItem),
                  value: roleItem,
                }))}
                selection={true}
                text={t(`models.affiliation.roleType.${values.role}.name`)}
              />
            </div>
            <div className="button-group">
              <Form.Button
                data-test="do-invite"
                type="submit"
                primary={true}
                disabled={isLoading}
              >
                {t('ui.action.inviteNow')}
              </Form.Button>
            </div>
          </Form.Field>
        </Form>
      </Styled>
    );
  },
);

const Styled = styled.div`
  .invite-form .button-group {
    margin: 3.5em 0 7.5em;
    text-align: center;
  }

  .ui.selection.dropdown.select-role {
    width: 65% !important;
  }

  .select-role-header {
    margin: 0 !important;
    font-size: 1.28571429em !important;
  }
`;
